import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { Box } from '../ui/foundation'
import { useAuth } from '../../hooks/useAuth'
import { useUserProfile } from '../../hooks/useUserProfile'
import { ToastContainer } from '../ui/composite/ToastContainer'
import { logError } from '../../utils/logger'
import { GlobalTopBar } from './GlobalTopBar'
import { MobileAuthenticatedTopBar } from '../mobile/MobileAuthenticatedTopBar'
import { useMobileDetection } from '../../hooks/useMobileDetection'

export interface AppLayoutProps {
  /** Main content to display */
  children: React.ReactNode

  /** User information for the sidebar */
  userAvatar?: string
  userName?: string
  userEmail?: string

  /** Additional className for the layout */
  className?: string

  /** Disable the standard content entrance animation for routes that need instant stability */
  disableContentEntrance?: boolean
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  userAvatar: _userAvatar,
  userName,
  userEmail,
  className,
  disableContentEntrance = false,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuth()
  const { profile } = useUserProfile()
  const { isPhone } = useMobileDetection()

  // Get display name with proper fallbacks
  const displayName =
    userName ||
    profile?.display_name ||
    user?.user_metadata?.display_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'User'

  const displayEmail = userEmail || user?.email || 'user@example.com'

  const handleAccountClick = () => {
    navigate('/account')
  }

  const handleOrdersClick = () => {
    navigate('/prints')
  }

  const handleSupportClick = () => {
    navigate('/support')
  }

  const handleSignOutClick = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      logError('AppLayout', 'Failed to sign out:', error)
    }
  }

  return (
    <Box
      as="div"
      className={cn(
        'flex h-screen w-full flex-col overflow-hidden bg-background-50',
        className
      )}
    >
      {isPhone ? (
        <MobileAuthenticatedTopBar />
      ) : (
        <GlobalTopBar
          userName={displayName}
          onAccountClick={handleAccountClick}
          onOrdersClick={handleOrdersClick}
          onSupportClick={handleSupportClick}
          onSignOutClick={handleSignOutClick}
        />
      )}

      <Box
        as="main"
        className="flex-1 min-w-0 overflow-auto"
        aria-label={`Content for ${displayEmail}`}
      >
        <div
          key={`${location.pathname}${location.search}`}
          className={cn(!disableContentEntrance && 'pb-app-content-enter')}
        >
          {children}
        </div>
      </Box>

      {/* Global Toast Container */}
      <ToastContainer />
    </Box>
  )
}

export default AppLayout
